import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';
import Reveal from '../../components/course/Reveal';
import PreviewVideo from '../../components/course/PreviewVideo';
import ScrollProgress from '../../components/course/ScrollProgress';
import '../courses/CoursePage.css';

const CURRENT_SLUG = 'ai-dashboard';

/**
 * Challenge index. Lists the current run and the archive of past runs, so the
 * recordings keep working as an evergreen content asset between challenges.
 */
const ChallengeIndexPage = () => {
  const [challenges, setChallenges] = useState([]);

  useEffect(() => {
    let mounted = true;
    const API_URL = getApiBaseUrl() || 'https://api.botspot.trade';
    (async () => {
      try {
        const res = await fetch(`${API_URL}/courses/session-groups`);
        if (!res.ok) throw new Error(String(res.status));
        const data = await res.json();
        const groups = Array.isArray(data) ? data : (data.sessionGroups || data.data || []);
        if (!mounted) return;
        setChallenges(groups.map((g) => ({
          slug: String(g.name || '').toLowerCase().replace(/[^a-z0-9]+/g, '-'),
          name: g.name,
          isUpcoming: g.scheduledAt ? new Date(g.scheduledAt).getTime() > Date.now() : false,
          sessionCount: g.sessionCount,
        })));
      } catch {
        /* Falls back to the evergreen call to action below. */
      }
    })();
    return () => { mounted = false; };
  }, []);

  const upcoming = challenges.filter((c) => c.isUpcoming);
  const past = challenges.filter((c) => !c.isUpcoming);

  return (
    <div className="course-page" data-testid="challenge-index">
      <ScrollProgress />
      <Helmet>
        <title>Free AI Trading Challenges | BotSpot</title>
        <meta name="description" content="Free live challenges: build real AI trading tools with Claude Code alongside Rob, in a week." />
      </Helmet>

      <header className="cp-hero">
        <div className="cp-hero-copy">
          <Reveal><p className="cp-eyebrow">Free · Live with Rob</p></Reveal>
          <Reveal delay={60}><h1>Build something real, in a week.</h1></Reveal>
          <Reveal delay={120}>
            <p className="cp-sub">
              Each challenge is a live kickoff, six short daily assignments, and a live demo day where
              Rob reviews what everyone built. Free to join, no coding experience required.
            </p>
          </Reveal>
          <Reveal delay={180}>
            <Link className="cp-btn-primary" to={`/challenges/${CURRENT_SLUG}`}>Join the current challenge</Link>
          </Reveal>
        </div>
        <Reveal delay={160} className="cp-hero-art">
          <img src="/images/courses/ai-stock-dashboard.webp"
            alt="An AI-built stock dashboard with position rows and a what-changed feed"
            width="1280" height="714" loading="eager" />
        </Reveal>
      </header>

      <section className="cp-section cp-what-is">
        <Reveal><h2>What a challenge is</h2></Reveal>
        <Reveal delay={70}>
          <p className="cp-sub">
            A free, live, one-week build. Rob opens with a session where he builds the thing from
            scratch on camera, you get one short assignment a day for six days, and then everyone
            comes back for a live demo day where Rob reviews what people made and unsticks anyone
            who got stuck. No coding experience required.
          </p>
        </Reveal>
      </section>

      <PreviewVideo
        heading="See how Rob teaches"
        blurb="A clip from a real session, so you know what you are signing up for."
      />

      {upcoming.length > 0 && (
        <section className="cp-section">
          <Reveal><h2>Coming up</h2></Reveal>
          <ul className="cp-archive">
            {upcoming.map((c) => (
              <Reveal as="li" key={c.slug}>
                <strong>{c.name}</strong>
                <br />
                <Link to={`/challenges/${c.slug}`}>Register free</Link>
              </Reveal>
            ))}
          </ul>
        </section>
      )}

      {past.length > 0 && (
        <section className="cp-section">
          <Reveal><h2>Past challenges</h2></Reveal>
          <ul className="cp-archive">
            {past.map((c) => (
              <Reveal as="li" key={c.slug}>
                <strong>{c.name}</strong>
                <br />
                <Link to={`/challenges/${c.slug}`}>Watch the recordings</Link>
              </Reveal>
            ))}
          </ul>
        </section>
      )}

      <section className="cp-section cp-final">
        <Reveal>
          <h2>Want the full bootcamp?</h2>
          <p className="cp-muted">Five live weeks, including connecting your broker and placing reviewed trades through your AI.</p>
          <Link className="cp-btn-primary" to="/courses/ai-trading-bootcamp">See the AI Trading Bootcamp</Link>
        </Reveal>
      </section>
    </div>
  );
};

export default ChallengeIndexPage;
